import React, {
    ReactElement, useState,
} from "react";
import { Form, Modal } from "react-bootstrap";
import { BsKanban, BsKanbanFill } from "react-icons/bs";
import { FaSortAmountDownAlt, FaSortAmountUp } from "react-icons/fa";
import { MdViewCompact } from "react-icons/md";
import { RiCloseLine, RiDashboardFill } from "react-icons/ri";
import { TiSortAlphabetically } from "react-icons/ti";
import usePortal from "../../../contexts/PortalContext";
import {
    Dashboard, DashboardGroupBy, DashboardSettingsInput,
    DashboardSortBy, DashboardView, ListStatesDocument, Permission, useGetAllTicketStatesQuery,
    useUpdateDashboardMutation,
} from "../../../__generated__/api";
import Loading from "../../loading/Loading";
import TicketStates from "../../pickers/ticket-states/TicketStates";
import Select from "../../select/Select";
import { hasDisabledNotifications, resetNotifications } from "../../../contexts/NotificationContext";

export interface Props {
    onClose: () => void,
}

export const GROUP_BY_TEXT = {
    [DashboardGroupBy.Assignee]: {
        text: "Assignee",
        permission: Permission.ChatsReadAll,
    },
    [DashboardGroupBy.User]: {
        text: "User",
        permission: Permission.ChatsRead,
    },
    [DashboardGroupBy.Priority]: {
        text: "Priority",
        permission: Permission.ChatsRead,
    },
    [DashboardGroupBy.Channel]: {
        text: "Channel",
        permission: Permission.ChatsRead,
    },
    [DashboardGroupBy.CompanyGroup]: {
        text: "Company/Group",
        permission: Permission.ChatsRead,
    },
    [DashboardGroupBy.Queue]: {
        text: "Queue",
        permission: Permission.ChatsRead,
    },
    [DashboardGroupBy.NoGroup]: {
        text: "Dont group",
        permission: Permission.ChatsRead,
    },
};

export const VIEW_TICKETS_TEXT = {
    [DashboardGroupBy.User]: "User",
    [DashboardGroupBy.Priority]: "Priority",
    [DashboardGroupBy.Channel]: "Channel",
    [DashboardGroupBy.NoGroup]: "Dont group",
};

export const VIEW_TEXT = {
    [DashboardView.Kanban]: "Kanban",
    [DashboardView.KanbanWrapped]: "Wrapped Kanban",
    [DashboardView.Wall]: "Wall",
    [DashboardView.Compact]: "Compact",
};

export const VIEW_ICONS = {
    [DashboardView.Kanban]: <BsKanban />,
    [DashboardView.KanbanWrapped]: <BsKanbanFill />,
    [DashboardView.Wall]: <RiDashboardFill />,
    [DashboardView.Compact]: <MdViewCompact />,

};

export const SORT_BY_TEXT_ICONS = {
    [DashboardSortBy.CreatedDateAscending]: (
        <>
            <FaSortAmountDownAlt />
            {" "}
            {" "}
            Created Date

        </>
    ),
    [DashboardSortBy.CreatedDateDescending]: (
        <>
            <FaSortAmountUp />
            {" "}
            Created Date
            {" "}

        </>
    ),
    [DashboardSortBy.LastResponseDateAscending]: (
        <>
            <FaSortAmountDownAlt />
            {" "}
            {" "}
            Last Response Date

        </>
    ),
    [DashboardSortBy.LastResponseDateDescending]: (
        <>
            <FaSortAmountUp />
            {" "}
            Last Response Date

        </>
    ),
    [DashboardSortBy.PriorityAscending]: (
        <>
            <FaSortAmountDownAlt />
            {" "}
            Priority

        </>
    ),
    [DashboardSortBy.PriorityDescending]: (
        <>
            <FaSortAmountUp />
            {" "}
            Priority
        </>
    ),
    [DashboardSortBy.TicketNumberAscending]: (
        <>
            <FaSortAmountDownAlt />
            {" "}
            Ticket Number

        </>
    ),
    [DashboardSortBy.TicketNumberDescending]: (
        <>
            <FaSortAmountUp />
            {" "}
            Ticket Number
        </>
    ),
    [DashboardSortBy.Manual]: (
        <>
            <TiSortAlphabetically />
            {" "}
            Manual
        </>
    ),
};

export const SORT_BY_TEXT = {
    [DashboardSortBy.CreatedDateAscending]: "Created Date Ascending",
    [DashboardSortBy.CreatedDateDescending]: "Created Date Descending",
    [DashboardSortBy.LastResponseDateAscending]: "Last Response Date Ascending",
    [DashboardSortBy.LastResponseDateDescending]: "Last Response Date Descending",
    [DashboardSortBy.PriorityAscending]: "Priority Ascending",
    [DashboardSortBy.PriorityDescending]: "Priority Descending",
    [DashboardSortBy.TicketNumberAscending]: "Ticket Number Ascending",
    [DashboardSortBy.TicketNumberDescending]: "Ticket Number Descending",
    [DashboardSortBy.Manual]: "Manual",
};

function DashboardSettings({ dashboard }: {dashboard: Dashboard}): ReactElement {
    const [groupBy, setGroupBy] = useState(dashboard.settings?.groupBy ?? DashboardGroupBy.Channel);
    const [dashboardView, setDashboardView] = useState(dashboard.settings?.view ?? DashboardView.Kanban);

    const [sortBy, setSortBy] = useState(dashboard.settings?.sortBy ?? DashboardSortBy.CreatedDateAscending);
    const [ticketStates, setTicketStates] = useState(dashboard.ticketStates
        .sortBy(row => row.locationIndex)
        .map(row => row.ticketStateId) ?? []);

    const [lastConversationCount, setLastConversationCount] = useState(dashboard.settings?.lastConversationCount ?? 5);

    const [saveDashboard] = useUpdateDashboardMutation();
    const { permissions } = usePortal();

    const save = (settings: Partial<DashboardSettingsInput>) => {
        const copy = {
            ...dashboard.settings,
            ...settings,
        };
        delete copy.__typename;
        saveDashboard({
            refetchQueries: [
                { query: ListStatesDocument },
            ],
            variables: {
                dashboard: {
                    settings: copy,
                },
            },
        });
    };

    const saveTicketStates = (states: string[]) => {
        const previousStates = dashboard.ticketStates.toIdMap("ticketStateId", row => ({ ...row, __typename: undefined }));
        saveDashboard({
            variables: {
                dashboard: {
                    ticketStates: states.map((stateId, index) => previousStates[stateId] ?? {
                        locationIndex: index,
                        ticketStateId: stateId,
                    }),
                },
            },
        });
    };

    return (
        <div className="flex-grow-1 p-3 d-flex flex-column">
            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Columns</Form.Label>
                <TicketStates
                    isMulti
                    onSelected={states => {
                        setTicketStates(states);
                        saveTicketStates(states);
                    }}
                    value={ticketStates}
                />
                <Form.Text>
                    Which columns should be displayed in your dashboard.
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Dashboard View</Form.Label>
                <Select
                    options={Object.entries(VIEW_TEXT).map(([value, label]) => ({
                        value,
                        label,
                    }))}
                    value={dashboardView ? {
                        value: dashboardView,
                        label: VIEW_TEXT[dashboardView],
                    } : undefined}
                    onChange={selected => {
                        setDashboardView(selected.value as DashboardView);
                        save({ view: selected.value as DashboardView });
                    }}
                />
                <Form.Text>
                    Which dashboard view should be used?
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Group tickets by</Form.Label>
                <Select
                    options={Object.entries(GROUP_BY_TEXT)
                        .filter(([, value]) => permissions.has(value.permission))
                        .map(([key, value]) => ({
                            value: key,
                            label: value.text,
                        }))}
                    value={groupBy ? {
                        value: groupBy,
                        label: GROUP_BY_TEXT[groupBy].text,
                    } : undefined}
                    onChange={selected => {
                        setGroupBy(selected.value as DashboardGroupBy);
                        save({ groupBy: selected.value as DashboardGroupBy });
                    }}
                />
                <Form.Text>
                    How should the tickets in this dashboard be grouped?
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Sort Tickets by</Form.Label>

                <Select
                    options={Object.entries(SORT_BY_TEXT).map(([value, label]) => ({
                        value,
                        label,
                    }))}
                    value={sortBy ? {
                        value: sortBy,
                        label: SORT_BY_TEXT[sortBy],
                    } : undefined}
                    onChange={selected => {
                        setSortBy(selected.value as DashboardSortBy);
                        save({ sortBy: selected.value as DashboardSortBy });
                    }}
                />
                <Form.Text>
                    How should the tickets in this dashboard be sorted?
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Previous Conversation Count</Form.Label>
                <Form.Control
                    type="number"
                    placeholder="5"
                    required
                    max={20}
                    min={0}
                    onChange={evt => {
                        const value = Number.parseInt(evt.target.value, 10);
                        setLastConversationCount(value);
                        save({ lastConversationCount: value });
                    }}
                    value={lastConversationCount}
                />
                <Form.Text>
                    The number of previously opened conversations to display in the bottom right hand corner. To
                    disable this setting, please set this to 0
                </Form.Text>
            </Form.Group>

            {hasDisabledNotifications() && (
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder d-block">Reset Notifications</Form.Label>
                    <button className="btn btn-primary" type="button" onClick={resetNotifications}>
                        Reset Notifications
                    </button>
                    <Form.Text className="d-block">
                        Looks like you disabled notifications in the agent view. Use this to reneable notifications.
                    </Form.Text>
                </Form.Group>
            )}

        </div>
    );
}

export default function ManageColumns({ onClose }: Props): ReactElement {
    const {
        data, loading,
    } = useGetAllTicketStatesQuery({
        fetchPolicy: "no-cache",
    });

    return (
        <Modal
            show
            size="lg"
            fullscreen="lg-down"
            onHide={onClose}
            dialogClassName="card bg-white shadow border-0"
            contentClassName="h-100 border-0"

        >
            <Modal.Body className="h-100 w-100 m-0 p-0 d-flex flex-column">
                <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                    <div>
                        Dashboard Settings
                    </div>
                    <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={onClose}>
                        <RiCloseLine className="text-light" />
                    </button>
                </div>
                {(loading || !data) ? <Loading /> : (
                    <DashboardSettings dashboard={data.dashboard as Dashboard} />
                )}

            </Modal.Body>
        </Modal>
    );
}
